/**
 * Month range rail — twelve month cells read as one bar. Click a month for
 * that month alone; drag across (or shift-click) for a range; the presets
 * jump to the full year or a quarter. Arrow keys move a single month,
 * shift+arrow grows the range.
 */

import React, { useEffect, useRef, useState } from 'react';

export type MonthRange = [number, number]; // inclusive, 0-based

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PRESETS: { label: string; range: MonthRange }[] = [
  { label: 'Full year', range: [0, 11] },
  { label: 'Q1', range: [0, 2] },
  { label: 'Q2', range: [3, 5] },
  { label: 'Q3', range: [6, 8] },
  { label: 'Q4', range: [9, 11] },
];

export function monthRangeLabel([start, end]: MonthRange, year: number): string {
  if (start === 0 && end === 11) return `Full year ${year}`;
  if (start === end) return `${SHORT[start]} ${year}`;
  return `${SHORT[start]} – ${SHORT[end]} ${year}`;
}

interface MonthRangeRailProps {
  value: MonthRange;
  onChange: (next: MonthRange) => void;
  /** 0-based months that are closed (actuals) — marked with a dot. */
  closedMonths?: number[];
}

export function MonthRangeRail({ value, onChange, closedMonths = [] }: MonthRangeRailProps) {
  const [start, end] = value;
  // Anchor of the drag / shift-click in progress.
  const anchor = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => { window.removeEventListener('pointerup', stop); window.removeEventListener('pointercancel', stop); };
  }, [dragging]);

  const span = (a: number, b: number): MonthRange => [Math.min(a, b), Math.max(a, b)];
  const clamp = (i: number) => Math.max(0, Math.min(11, i));

  const onDown = (i: number, e: React.PointerEvent) => {
    if (e.shiftKey && anchor.current !== null) { onChange(span(anchor.current, i)); return; }
    anchor.current = i;
    setDragging(true);
    onChange([i, i]);
  };
  const onEnter = (i: number) => {
    if (dragging && anchor.current !== null) onChange(span(anchor.current, i));
  };
  const onKey = (e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    if (e.shiftKey) onChange(step > 0 ? [start, clamp(end + 1)] : [start, Math.max(start, end - 1)]);
    else { const i = clamp((step > 0 ? end : start) + step); anchor.current = i; onChange([i, i]); }
  };

  const closed = new Set(closedMonths);
  const isPreset = (r: MonthRange) => r[0] === start && r[1] === end;

  return (
    <div className="flex items-center gap-2.5 flex-wrap">
      <div
        className="inline-flex items-stretch rounded-lg p-0.5 select-none flex-none"
        style={{ background: 'var(--lodge-onyx-50)', touchAction: 'none' }}
        role="group"
        aria-label="Months in the period"
        tabIndex={0}
        onKeyDown={onKey}
      >
        {SHORT.map((label, i) => {
          const on = i >= start && i <= end;
          const first = i === start;
          const last = i === end;
          return (
            <button
              key={label}
              type="button"
              tabIndex={-1}
              onPointerDown={(e) => onDown(i, e)}
              onPointerEnter={() => onEnter(i)}
              aria-pressed={on}
              title={closed.has(i) ? `${label} · closed (actuals)` : label}
              className="relative h-[26px] w-[38px] text-[12px] transition-colors"
              style={{
                fontWeight: on ? 600 : 500,
                color: on ? 'var(--lodge-orange-600)' : 'var(--lodge-onyx-500)',
                background: on ? 'var(--lodge-white)' : 'transparent',
                borderTop: `1px solid ${on ? 'var(--lodge-orange-200)' : 'transparent'}`,
                borderBottom: `1px solid ${on ? 'var(--lodge-orange-200)' : 'transparent'}`,
                borderLeft: `1px solid ${first ? 'var(--lodge-orange-200)' : 'transparent'}`,
                borderRight: `1px solid ${last ? 'var(--lodge-orange-200)' : 'transparent'}`,
                borderRadius: `${first ? 6 : 0}px ${last ? 6 : 0}px ${last ? 6 : 0}px ${first ? 6 : 0}px`,
                cursor: dragging ? 'ew-resize' : 'pointer',
              }}
            >
              {label}
              {closed.has(i) && (
                <span className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{ bottom: 2, width: 3, height: 3, background: on ? 'var(--lodge-orange-600)' : 'var(--lodge-onyx-300)' }} />
              )}
            </button>
          );
        })}
      </div>
      <div className="fv3-seg inline-flex items-center rounded-lg p-0.5 flex-none" style={{ background: 'var(--lodge-onyx-50)' }} role="group" aria-label="Period shortcuts">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => { anchor.current = p.range[0]; onChange(p.range); }}
            className={`fv3-seg-btn px-2.5 py-1 text-xs rounded-md transition-colors ${isPreset(p.range) ? 'fv3-seg-on font-semibold' : 'font-medium'}`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default MonthRangeRail;
